import { DocumentNode, gql, useMutation } from '@apollo/client';
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';

import { DatePicker } from '@/components/DatePicker';
import { ReadOnly, ReadOnlyLabel, ReadOnlyValue } from '@/components/ReadOnly';
import { CompactCheckbox } from '@/imports/ui/chakra/components/CompactCheckbox';

import { ExtensionModal_MembershipTermDataFragment } from './graphql-types/ExtensionModal_MembershipTermDataFragment';
import {
  ExtensionModal_ModifyMembershipTermEndDate,
  ExtensionModal_ModifyMembershipTermEndDateVariables,
} from './graphql-types/ExtensionModal_ModifyMembershipTermEndDate';

dayjs.extend(minMax);

const PATCH_TERM = gql`
  mutation ExtensionModal_ModifyMembershipTermEndDate(
    $input: ModifyMembershipTermEndDateInput!
  ) {
    modifyMembershipTermEndDate(input: $input) {
      ... on ModifyMembershipTermEndDateSuccess {
        membershipTerm {
          _id
          startDate
          endDate
          importantDates {
            date
            ... on SimpleMembershipTermImportantDate {
              date
              type
            }
            ... on PaymentMembershipTermImportantDate {
              date
              paymentType
            }
            ... on AutoPayMembershipTermImportantDate {
              date
              autoPayType
            }
          }
        }
      }
    }
  }
`;

type ExtensionModalFormValues = {
  endDate?: Date | null;
  isMembershipEndless?: boolean;
};

export const ExtensionModal: React.FC<{
  membershipTerm: ExtensionModal_MembershipTermDataFragment;
  displayName: string;
  onClose: () => void;
  isOpen: boolean;
}> & { membershipTermDataFragment: DocumentNode } = ({
  membershipTerm,
  displayName,
  onClose,
  isOpen,
}) => {
  const toast = useToast();

  const [modifyMembershipTermEndDate, modifyMembershipTermEndDateResult] =
    useMutation<
      ExtensionModal_ModifyMembershipTermEndDate,
      ExtensionModal_ModifyMembershipTermEndDateVariables
    >(PATCH_TERM, {
      onError: () =>
        toast({
          title: 'An unknown error occurred',
          status: 'error',
        }),
      onCompleted: (data) => {
        if (
          data.modifyMembershipTermEndDate.__typename ===
          'ModifyMembershipTermEndDateSuccess'
        ) {
          onClose();
          toast({
            title: 'You successfully updated this membership term!',
            status: 'success',
          });
        } else {
          toast({
            title: 'An unknown error occurred',
            status: 'error',
          });
        }
      },
      refetchQueries: ['User_Query'],
      awaitRefetchQueries: true,
    });

  const {
    control,
    handleSubmit,
    register,
    getValues,
    watch,
    formState: { errors },
  } = useForm<ExtensionModalFormValues>({
    defaultValues: {
      endDate: membershipTerm.endDate
        ? new Date(membershipTerm.endDate)
        : undefined,
      isMembershipEndless: membershipTerm.isMembershipEndless,
    },
  });

  const watchIsMembershipEndless = watch('isMembershipEndless');

  const onSubmit = handleSubmit((data) => {
    modifyMembershipTermEndDate({
      variables: {
        input: {
          membershipTermId: membershipTerm._id,
          endDate: data.isMembershipEndless ? null : data.endDate?.toString(),
        },
      },
    });
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Extend the current membership term</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing="6">
            <Text color="gray.600">
              {`Extend ${displayName}'s membership term by updating the
                current membership term end date.`}
            </Text>

            <ReadOnly>
              <ReadOnlyLabel>Membership type</ReadOnlyLabel>
              <ReadOnlyValue>
                {membershipTerm.membershipType.name}
              </ReadOnlyValue>
            </ReadOnly>

            <ReadOnly>
              <ReadOnlyLabel>Membership term start</ReadOnlyLabel>
              <ReadOnlyValue>
                {dayjs(membershipTerm.startDate).format('MMM D, YYYY')}
              </ReadOnlyValue>
            </ReadOnly>

            <FormControl
              isDisabled={
                watchIsMembershipEndless ||
                modifyMembershipTermEndDateResult.loading
              }
              isInvalid={!!errors.endDate}
              isRequired={!watchIsMembershipEndless}
            >
              <FormLabel>Update Membership Term End</FormLabel>
              <Controller
                control={control}
                name="endDate"
                render={({ field: { value, onChange, ref } }) => (
                  <DatePicker
                    selected={value}
                    customInput={<Input ref={ref} />}
                    onChange={onChange}
                    autoComplete="off"
                    minDate={dayjs(membershipTerm.endDate)
                      .add(1, 'day')
                      .toDate()}
                    dateFormat="MMM d, yyyy"
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                  />
                )}
                rules={{
                  validate: {
                    required: (value) =>
                      getValues('isMembershipEndless') ||
                      !!value ||
                      'This is a required field',
                  },
                }}
              />
              <FormErrorMessage>{errors.endDate?.message}</FormErrorMessage>
            </FormControl>

            <Controller
              name="isMembershipEndless"
              control={control}
              rules={{ deps: ['endDate'] }}
              render={({ field: { value, onChange, ref, onBlur } }) => {
                return (
                  <CompactCheckbox
                    isChecked={value}
                    onChange={(event) => onChange(event.target.checked)}
                    ref={ref}
                    onBlur={onBlur}
                    isDisabled={modifyMembershipTermEndDateResult.loading}
                  >
                    {`This membership term doesn't have an end`}
                  </CompactCheckbox>
                );
              }}
            />
          </Stack>
        </ModalBody>

        <ModalFooter>
          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="outline"
              colorScheme="gray"
              onClick={onClose}
              isDisabled={modifyMembershipTermEndDateResult.loading}
            >
              Cancel
            </Button>

            <Button
              isLoading={modifyMembershipTermEndDateResult.loading}
              onClick={onSubmit}
            >
              Save Changes
            </Button>
          </Stack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

ExtensionModal.membershipTermDataFragment = gql`
  fragment ExtensionModal_MembershipTermDataFragment on MembershipTermV2 {
    _id
    endDate
    startDate
    isMembershipEndless
    membershipType {
      _id
      name
    }
  }
`;
